import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

const cadastroSearchSchema = z.object({
  convite: z.string().optional(),
  tipo: z.string().optional(),
});

export const Route = createFileRoute("/cadastro")({
  validateSearch: (search) => cadastroSearchSchema.parse(search),
  component: CadastroPage,
});

function CadastroPage() {
  const { convite, tipo: searchTipo } = Route.useSearch();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [inviteStatus, setInviteStatus] = useState<"none" | "invalid" | "valid">("none");
  const [inviteData, setInviteData] = useState<any>(null);

  // Form states
  const [nomeStudio, setNomeStudio] = useState("");
  const [nomeResponsavel, setNomeResponsavel] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateInvite = async () => {
      if (!convite) {
        setInviteStatus("none");
        setIsValidating(false);
        return;
      }

      try {
        // valida sem expor a tabela de convites (função pública SECURITY DEFINER).
        // fallback para leitura direta enquanto a função não existir no banco.
        let info: any = null;
        const { data, error: rpcError } = await supabase.rpc("validar_convite", { p_codigo: convite });
        if (!rpcError && data) {
          info = Array.isArray(data) ? data[0] : data;
        } else {
          const { data: row } = await supabase
            .from("convites")
            .select("tipo, usado, expira_em")
            .eq("codigo", convite)
            .maybeSingle();
          if (row) {
            const expirado = row.expira_em && new Date(row.expira_em) < new Date();
            info = { valido: !row.usado && !expirado, tipo: row.tipo, nome: null, telefone: null };
          }
        }

        if (!info || !info.valido) {
          setInviteStatus("invalid");
        } else {
          setInviteData({ tipo: info.tipo });
          if (info.tipo === "tatuador") {
            setNomeStudio("Studio Vinculado");
            if (info.nome) setNomeResponsavel(info.nome);
            if (info.telefone) setTelefone(info.telefone);
            if (!info.nome) {
              const { data: prereg } = await supabase
                .from("equipe")
                .select("nome, telefone")
                .eq("codigo_convite", convite)
                .maybeSingle();
              if (prereg?.nome) setNomeResponsavel(prereg.nome);
              if (prereg?.telefone) setTelefone(prereg.telefone);
            }
          }
          setInviteStatus("valid");
        }
      } catch (err) {
        setInviteStatus("invalid");
      } finally {
        setIsValidating(false);
      }
    };

    validateInvite();
  }, [convite]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("As senhas não coincidem");
      setIsLoading(false);
      return;
    }

    if (password.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres");
      setIsLoading(false);
      return;
    }

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/cadastro?convite=${convite}`,
        },
      });

      if (authError) {
        const msg = authError.message.toLowerCase();
        if (msg.includes("rate limit") || msg.includes("429") || authError.status === 429) {
          setError("O envio de e-mails está temporariamente limitado. Aguarde alguns minutos e tente novamente.");
        } else if (msg.includes("already registered") || msg.includes("user already exists")) {
          setError("Este e-mail já está cadastrado. Tente fazer login.");
        } else if (msg.includes("invalid") && msg.includes("email")) {
          setError("E-mail inválido. Use um endereço de e-mail real.");
        } else {
          setError(authError.message);
        }
        return;
      }

      if (!authData?.user) {
        setError("Não foi possível criar a conta. Tente novamente.");
        return;
      }

      // Garante sessão ativa (com "Confirm email" desligado o signUp já retorna sessão)
      let userId = authData.user.id;
      if (!authData.session) {
        const { data: loginData, error: loginErr } = await supabase.auth.signInWithPassword({ email, password });
        if (loginErr || !loginData?.session) {
          const { data: realId } = await supabase.rpc("confirmar_email_convite", { p_email: email, p_codigo: convite });
          if (realId) userId = realId as string;
          const retry = await supabase.auth.signInWithPassword({ email, password });
          if (retry.error) {
            setError("Conta criada, mas não foi possível entrar automaticamente. Tente fazer login.");
            return;
          }
        } else {
          userId = loginData.user.id;
        }
      }

      const isTatuadorInvite = inviteData?.tipo === "tatuador";

      if (isTatuadorInvite) {
        // A RPC cria o perfil, vincula ao pré-cadastro da equipe e marca o convite como usado
        const { error: rpcErr } = await supabase.rpc("finalizar_cadastro_tatuador", {
          p_codigo: convite,
          p_nome: nomeResponsavel,
          p_telefone: telefone,
        });
        if (rpcErr) {
          setError("Erro ao finalizar cadastro: " + rpcErr.message);
          return;
        }
      } else {
        const { error: userError } = await supabase.from("usuarios").upsert({
          id: userId,
          nome: nomeResponsavel,
          email,
          telefone,
          role: "studio",
          status: "ativo",
          studio_id: userId,
        }, { onConflict: "id" });
        if (userError) {
          setError("Erro ao salvar dados do usuário: " + userError.message);
          return;
        }

        const { error: configError } = await supabase.from("configuracoes").insert({
          nome_studio: nomeStudio,
          studio_id: userId,
        });
        if (configError && !configError.message.toLowerCase().includes("duplicate")) {
          setError("Erro ao salvar configurações do studio: " + configError.message);
          return;
        }

        // marca o convite como usado (RPC — a tabela convites não é mais gravável direto)
        const { error: marcaErr } = await supabase.rpc("marcar_convite_usado", { p_codigo: convite });
        if (marcaErr) {
          await supabase
            .from("convites")
            .update({ usado: true, usado_em: new Date().toISOString() })
            .eq("codigo", convite);
        }
      }

      toast.success("Conta criada com sucesso!");
      navigate({ to: "/" });
    } catch (err: any) {
      setError("Ocorreu um erro inesperado: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="animate-pulse text-foreground font-medium">Validando convite...</div>
      </div>
    );
  }

  if (inviteStatus === "none") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <Card className="w-full max-w-md bg-card border-border p-8 space-y-6 shadow-xl">
          <div className="flex justify-center">
            <AlertCircle className="text-primary w-16 h-16" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Acesso restrito</h2>
          <p className="text-muted-foreground">Entre em contato para contratar o sistema.</p>
          <Button
            onClick={() => navigate({ to: "/login" })}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12"
          >
            Voltar ao login
          </Button>
        </Card>
      </div>
    );
  }

  if (inviteStatus === "invalid") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <Card className="w-full max-w-md bg-card border-border p-8 space-y-6 shadow-xl">
          <div className="flex justify-center">
            <AlertCircle className="text-primary w-16 h-16" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Convite inválido</h2>
          <p className="text-muted-foreground">Este código de convite é inválido, expirou ou já foi utilizado.</p>
          <Button
            onClick={() => navigate({ to: "/login" })}
            className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12"
          >
            Voltar ao login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 py-12">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-foreground tracking-tight">
          Studio <span className="text-primary">Tattoo</span>
        </h1>
      </div>

      <Card className="w-full max-w-md bg-card border-border shadow-sm">
        <CardHeader className="pt-8 pb-4 text-center border-b border-border/50">
          <h2 className="text-xl font-bold text-foreground">Criar sua conta</h2>
          <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">Passo final para começar</p>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <form onSubmit={handleRegister} className="space-y-4">
            {inviteData?.tipo !== "tatuador" && (
              <div className="space-y-2">
                <Label htmlFor="nomeStudio" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                  Nome do Studio
                </Label>
                <Input
                  id="nomeStudio"
                  placeholder="Ex: My Tattoo Studio"
                  value={nomeStudio}
                  onChange={(e) => setNomeStudio(e.target.value)}
                  required
                  className="bg-card border-border text-foreground focus:border-primary h-12 transition-all"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="nomeResponsavel" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                Nome Completo do Responsável
              </Label>
              <Input
                id="nomeResponsavel"
                placeholder="Seu nome completo"
                value={nomeResponsavel}
                onChange={(e) => setNomeResponsavel(e.target.value)}
                required
                className="bg-card border-border text-foreground focus:border-primary h-12 transition-all"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-card border-border text-foreground focus:border-primary h-12 transition-all"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefone" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                Telefone
              </Label>
              <Input
                id="telefone"
                placeholder="(00) 00000-0000"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className="bg-card border-border text-foreground focus:border-primary h-12 transition-all"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-card border-border text-foreground focus:border-primary h-12 transition-all pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-muted-foreground font-bold uppercase text-[10px] tracking-widest">
                  Confirmar Senha
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-card border-border text-foreground focus:border-primary h-12 transition-all"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-primary text-sm bg-primary/10 p-3 rounded-lg border border-primary/20">
                <AlertCircle size={16} />
                <p>{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-12 shadow-lg shadow-primary/20 transition-all active:scale-[0.98] disabled:opacity-50 mt-2"
            >
              {isLoading ? "Criando conta..." : "Criar conta"}
            </Button>
          </form>

          <div className="text-center pt-2 border-t border-border/50 mt-6">
            <Link to="/login" className="text-muted-foreground hover:text-foreground text-sm transition-colors">
              Já tem conta? <span className="text-primary font-semibold underline decoration-border underline-offset-4">Entrar</span>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
